import type {
  Locale,
  Section,
  SectionFormFragment,
} from '@seek/cmsu-cms-connect';
import {
  Article,
  Breadcrumbs,
  SectionForm,
  SubscriptionForm,
  useTargetBlank,
  CMSUProvider,
} from '@seek/cmsu-components';
import { getArticleFeatureImage } from '@seek/cmsu-components/src/helpers/articleHelper';
import { getBreadcrumbItems } from '@seek/cmsu-components/src/modules/Breadcrumbs/breadcrumbHelper';
import type { AutofillableValue } from '@seek/cmsu-components/src/modules/CForm/types';
import type { DatalabSurvey } from '@seek/cmsu-components/src/modules/Talent-Attraction-Lab/datalabHelper';
import {
  Alert,
  Box,
  ContentBlock,
  PageBlock,
  Stack,
  Text,
} from 'braid-design-system';
import he from 'he';
import { useLoaderData } from 'react-router';

import type { ArticleLocalization } from 'src/helpers/loadersHelper';
import { transformSeoData } from 'src/helpers/seo';

import { WPT_SALESFORCE_API } from '../../config';
import { PageLayout } from '../components/PageLayout/PageLayout';
import { useAuth, useConfig } from '../shared/hooks';
import { usePageTracking } from '../shared/hooks/usePageTracking';
import type { LayoutProps } from '../shared/types';
import { getAutofillableValue } from '../shared/utils/getAutofillableValue';
import type { LocalesInfo } from '../shared/utils/getLocales';
import { enabledSubscriptionForm } from '../shared/utils/helper';

import { AdditionalContent } from './articleComponents/additionalContent';
import { SubscribeFormContactUsWrapper } from './pageComponents/SubscribeFormContactUsWrapper';

const layoutProps: LayoutProps = {
  title: 'Loading...',
};

// The Hygraph site that the article should be fetched from
type Props = {
  site: string;
  section: string;
  isPreview?: boolean;
};

export interface ArticleAdditionalData {
  datalabSurvey?: DatalabSurvey;
}

/**
 * TODO:
 * 2. add SEO meta data in <head>
 */
export const ArticlePage = ({ section, isPreview = false }: Props) => {
  useTargetBlank();

  // Get the params from context.
  const {
    melwaysLocale,
    fullURL,
    sourceName,
    country,
    language,
    privacyUrl,
    recaptchaKey,
    site,
  } = useConfig();

  // Get the article content by using the loader.
  const data = useLoaderData() as ArticleLocalization &
    LocalesInfo & { additionalData: ArticleAdditionalData };

  const {
    id,
    category: articleCategory,
    content,
    title,
    seo,
    locale,
    contactUs,
    formData,
    hideEmbeddedFormInSection,
    additionalContent,
  } = data;

  let contentText = he.decode(content.text.trim().replace(/\\n/g, ' '));
  contentText = contentText.trim().replace(/\n/g, '').replace(/  /g, '');

  // Get the feature image data from either Bynder or Hygraph sources
  const articleFeatureImage = getArticleFeatureImage(data);
  const { imageUrl } = articleFeatureImage;

  const pageSeo = transformSeoData(seo || {}, {
    title,
    image: imageUrl,
    description:
      contentText.substring(0, contentText.indexOf('.') + 1) || contentText,
  });

  const categoryName = articleCategory?.name || '';
  const categorySlug = articleCategory?.slug || '';
  const parentCategoryName = articleCategory?.parent?.name || '';
  const parentCategorySlug = articleCategory?.parent?.slug || '';
  const articleSection = articleCategory?.relatedCategorySection as Section;
  const parentSection =
    articleCategory?.relatedCategorySection?.parentSection?.sectionName;
  const relatedSectionName =
    articleCategory?.relatedCategorySection?.sectionName;
  const sectionDisplayName =
    articleCategory?.relatedCategorySection?.displayName;
  const parentSectionDisplayName =
    articleCategory?.relatedCategorySection?.parentSection?.displayName;
  const items = getBreadcrumbItems({
    locale: locale as Locale,
    section: relatedSectionName ? relatedSectionName : section,
    parentCategoryName,
    parentCategorySlug,
    categoryName,
    categorySlug,
    title,
    parentSection,
    sectionDisplayName,
    parentSectionDisplayName,
  });

  usePageTracking(title.toLocaleLowerCase(), categorySlug);

  const showSubscriptionForm = enabledSubscriptionForm(
    section,
    hideEmbeddedFormInSection?.sectionName,
  );

  const articleAdditionalContent =
    additionalContent || articleCategory?.additionalContent;

  const { caAccountContext, hirerAccountContext } = useAuth();
  const autofillableValue: AutofillableValue = getAutofillableValue(
    caAccountContext,
    hirerAccountContext,
  );

  return (
    <PageLayout
      layoutProps={{
        ...layoutProps,
        title,
        categoryName,
        section: {
          sectionName: articleSection?.sectionName,
          theme:
            articleSection?.theme === null ? undefined : articleSection?.theme,
          headerOptions:
            articleSection?.headerOptions === null
              ? undefined
              : articleSection?.headerOptions,
          footerOptions:
            articleSection?.footerOptions === null
              ? undefined
              : articleSection?.footerOptions,
        },
        seo: pageSeo,
        locales: data.locales,
      }}
    >
      <CMSUProvider
        config={{
          country,
          language,
          site,
          fullURL,
          sectionName: articleSection?.sectionName,
        }}
      >
        <Box background="surface">
          <Stack space="xlarge">
            <ContentBlock width="medium">
              <Stack space="none">
                <PageBlock>
                  <Breadcrumbs items={items} />
                  {articlePreviewInfo(isPreview)}
                  <Article articleData={data} />
                </PageBlock>
                {articlePreviewInfo(isPreview)}
              </Stack>
            </ContentBlock>
            <Stack
              space={{
                mobile: 'gutter',
                tablet: 'xlarge',
                desktop: 'xxlarge',
              }}
            >
              {articleAdditionalContent && (
                <Box paddingTop="xlarge">
                  <AdditionalContent
                    articleAdditionalContent={articleAdditionalContent}
                    articleId={id}
                    data={data}
                  />
                </Box>
              )}
              {contactUs && (
                <ContentBlock width="medium">
                  <SubscribeFormContactUsWrapper
                    contactUs={contactUs}
                    formData={formData}
                  />
                </ContentBlock>
              )}
              {showSubscriptionForm && (
                <SectionForm
                  sectionName={relatedSectionName}
                  sectionForm={
                    articleCategory?.relatedCategorySection
                      ?.sectionForm as SectionFormFragment
                  }
                  melwaysLocale={melwaysLocale}
                  sourceName={sourceName}
                  country={country}
                  privacyUrl={privacyUrl}
                  recaptchaKey={recaptchaKey}
                  site={site}
                  wptSalesForceApi={WPT_SALESFORCE_API}
                  autofillableValue={autofillableValue}
                />
              )}
              {showSubscriptionForm && (
                <Box
                  id="subscription-form"
                  background="surface"
                  overflow="hidden"
                  paddingY={{
                    mobile: 'medium',
                    tablet: 'large',
                    desktop: 'xlarge',
                  }}
                >
                  <SubscriptionForm
                    type={section}
                    locale={melwaysLocale}
                    sourceName={sourceName}
                    wptSalesForceApi={WPT_SALESFORCE_API}
                    country={country}
                  />
                </Box>
              )}
            </Stack>
          </Stack>
        </Box>
      </CMSUProvider>
    </PageLayout>
  );
};

const articlePreviewInfo = (isPreview: boolean) =>
  isPreview && (
    <Box paddingBottom="medium">
      <Alert tone="info">
        <Text>This is an Article Preview</Text>
      </Alert>
    </Box>
  );
